/** Helpers for line pricing on manual order create (variants + discount). */

export function normalizeOptionVariants(optionVariants) {
    if (optionVariants == null) return [];
    if (Array.isArray(optionVariants)) {
        return optionVariants.filter((v) => v != null && v !== '');
    }
    return Object.keys(optionVariants)
        .sort((a, b) => Number(a) - Number(b))
        .map((key) => optionVariants[key])
        .filter((v) => v != null && v !== '');
}

function optionLabel(option) {
    if (option == null) return '';
    if (typeof option === 'string' || typeof option === 'number') return String(option);
    if (option.product_option_value) return optionLabel(option.product_option_value);
    return String(option.name ?? option.value ?? '');
}

function variantOptionsToNames(options) {
    if (!Array.isArray(options)) return [];
    return options.map((o) => optionLabel(o)).filter(Boolean);
}

export function findProductVariant(product, optionVariants) {
    if (!product?.product_variants?.length) return null;
    const selected = normalizeOptionVariants(optionVariants);
    if (!selected.length) return null;

    const selectedKey = selected.map((v) => String(v).trim().toLowerCase()).join('|');

    const exactMatch =
        product.product_variants.find((variant) => {
            const names = variantOptionsToNames(variant?.options);
            if (JSON.stringify(names) === JSON.stringify(selected)) return true;
            const namesKey = names.map((v) => String(v).trim().toLowerCase()).join('|');
            return namesKey === selectedKey;
        }) ?? null;

    if (exactMatch) return exactMatch;

    // GET returns a variant's values in no fixed order, so also accept the same
    // values in another order. A partial selection matches no variant: picking
    // one that merely contains the value could order the wrong variant.
    const sortedKey = selected.map((v) => String(v).trim().toLowerCase()).sort().join('|');
    return (
        product.product_variants.find((variant) => {
            const names = variantOptionsToNames(variant?.options);
            return names.length === selected.length &&
                names.map((v) => String(v).trim().toLowerCase()).sort().join('|') === sortedKey;
        }) ?? null
    );
}

export function resolveUnitPrice(product, optionVariants) {
    if (!product) {
        return { unitPrice: 0, discountPrice: null };
    }
    const matched = findProductVariant(product, optionVariants);
    const unitPrice = Number(matched?.price ?? product.price) || 0;
    const discountRaw = matched?.discount_price ?? product.discount_price;
    const discountPrice =
        discountRaw != null && discountRaw !== '' && !Number.isNaN(Number(discountRaw))
            ? Number(discountRaw)
            : null;
    return { unitPrice, discountPrice };
}

export function getLineUnitPrice(unitPrice, discountPrice) {
    if (discountPrice != null && discountPrice > 0) {
        return discountPrice;
    }
    return unitPrice;
}

/** When catalog lookup fails, keep prices already stored on the form row. */
export function getRowLineUnitPrice(product, row) {
    if (product) {
        const { unitPrice, discountPrice } = resolveUnitPrice(product, row?.optionVariants);
        return getLineUnitPrice(unitPrice, discountPrice);
    }
    const storedDiscount =
        row?.discountPrice != null && row?.discountPrice !== ''
            ? Number(row.discountPrice)
            : null;
    if (storedDiscount != null && !Number.isNaN(storedDiscount) && storedDiscount > 0) {
        return storedDiscount;
    }
    return Number(row?.price) || 0;
}
